import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { gateNexusSubscription } from '@nsc/nexus-permissions/nexus-api/nexus-permission-gate.js';
import { NexusPermission } from '@nsc/nexus-permissions/platform-rbac/nexus-permissions.js';
import { from, switchMap } from 'rxjs';
import { RouteOptimizationResult } from '../entities/RouteOptimizationResult.js';
import { calculateStats } from './calculateRouteOptimizationStats.js';
import { getTotalProfitabilitySummary } from './getRouteOptimizationProfitability.js';
import { getRoutePlanningWorkingDayStats } from './getRoutePlanningWorkingDayStats.js';

/**
 * @entity RoutePlanningSummary
 */
@Entity()
export class RoutePlanningSummary extends AdornisEntity {
  static override _class = 'RoutePlanningSummary';

  static override resolveID(entity: RoutePlanningSummary): string {
    return entity._id;
  }

  @Field(_type => ID) _id!: string;
  @Field(_type => Date) createdAt: Maybe<Date>;
  @Field(_type => String) simulationType: Maybe<string>;
  @Field(_type => Float) profitability: Maybe<number>;
  @Field(_type => Float) longestWorkingDayMinutes: Maybe<number>;
  @Field(_type => Float) averageOvertimeMinutes: Maybe<number>;
  @Field(_type => Int) internalFeUsage: Maybe<number>;
  @Field(_type => Int) externalFeUsage: Maybe<number>;
  @Field(_type => Float) totalOvertimeCost: Maybe<number>;

  constructor(doc: PartialEntityData<RoutePlanningSummary>) {
    super(doc);
  }
}

export const getRoutePlanningSummaries = registerSubscription({
  operationName: 'getRoutePlanningSummaries',
  type: () => [RoutePlanningSummary],
  resolve:
    (skip: number, limit: number, filter: AdornisFilter) => (_gqlFields: BaseQLSelectionSet<RoutePlanningSummary>) => {
      return gateNexusSubscription(NexusPermission.RoutePlanning, () =>
        from(getRawCollection<EntityData<RouteOptimizationResult>>(RouteOptimizationResult._collectionName)).pipe(
          switchMap(async col => {
            const mongoOptions = filter?.getMongoOptions?.() ?? {};
            const sort = (mongoOptions.sort as Record<string, 1 | -1> | undefined) ?? { createdAt: -1 };
            const docs = await col
              .find(
                { type: 'Dell' },
                {
                  projection: {
                    _id: 1,
                    createdAt: 1,
                    simulationType: 1,
                    routes: 1,
                    fieldEngineersAvailable: 1,
                    partnerFieldEngineersAvailable: 1,
                  },
                },
              )
              .sort(sort)
              .skip(skip ?? 0)
              .limit(limit ?? 50)
              .toArray();

            return docs.map(doc => {
              const planning = new RouteOptimizationResult(doc);
              const workingDayStats = getRoutePlanningWorkingDayStats(planning);
              const profitabilitySummary = getTotalProfitabilitySummary(planning);
              const feStats = calculateStats(planning);

              return new RoutePlanningSummary({
                _id: planning._id,
                createdAt: planning.createdAt,
                simulationType: planning.simulationType,
                profitability: profitabilitySummary.profitDelta,
                longestWorkingDayMinutes: workingDayStats.longestWorkingDayMinutes,
                averageOvertimeMinutes: workingDayStats.averageOvertimeMinutes,
                internalFeUsage: feStats.totalAssignedInternalFEs,
                externalFeUsage: feStats.totalAssignedPartnerFEs,
                totalOvertimeCost: profitabilitySummary.overtimeCost,
              });
            });
          }),
        ),
      );
    },
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => AdornisFilter },
  ],
});
